import { FC, MutableRefObject, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Result } from 'antd';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { isMobile } from 'utils/is-mobile';
import { capitalize } from 'utils/string-manipulation/capitalize';
import { GlobalIcon } from 'components/GlobalIcon';
import { Button } from 'newcomponents/UI/Button';
import { useContentHeight } from 'hooks/useContentHeight';
import { getMenuJsonData } from 'store/selectors/app-data';
import { ResultSubTitle, ResultTitle } from '../style';
import './index.less';

const EXTRA_HEIGHT = 20;

type Props = {
  isLive?: boolean;
  cover?: boolean;
  withLink?: boolean;
  isEvent?: boolean;
  calendar?: boolean;
  isESport?: boolean;
  useFullContentHeight?: boolean;
  noGameText?: string;
};

export const EmptyMatches: FC<Props> = ({
  isLive,
  cover,
  withLink,
  isEvent,
  calendar,
  isESport,
  useFullContentHeight = false,
  noGameText
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const menuJsonData = useSelector(getMenuJsonData);
  const contentHeight = useContentHeight(false, EXTRA_HEIGHT);
  const resultRef = useRef() as MutableRefObject<HTMLInputElement>;

  const getType = (register: 'u' | 'l' | 'c' = 'c', reverse = false) => {
    const type = (reverse ? !isLive : isLive)
      ? 'live'
      : calendar
      ? 'calendar'
      : 'prematch';

    switch (register) {
      case 'u':
        return type.toUpperCase();
      case 'l':
        return type.toLowerCase();
      case 'c':
        return capitalize(type);
    }
  };

  useEffect(() => {
    if (isMobile()) {
      return;
    }

    const elem = resultRef.current.closest(
      `[class*=${isEvent ? 'EventsWrapper' : 'SBCol'}]`
    ) as HTMLElement;

    const prevPosition = elem?.style.position;
    const prevOverflow = elem?.style.overflow;

    if (elem) {
      elem.style.position = 'relative';
      elem.style.overflow = 'initial';
    }

    return () => {
      if (elem) {
        elem.style.position = prevPosition;
        elem.style.overflow = prevOverflow;
      }
    };
  }, [resultRef]);

  return (
    <Result
      title={<ResultTitle>{t('sportsbook.noGame')}</ResultTitle>}
      {...(cover
        ? {
            style: {
              background: 'var(--v3-black-2)',
              position: isMobile() ? 'static' : 'absolute',
              top: 0,
              bottom: 0,
              width:
                isMobile() || !withLink || isEvent || isESport
                  ? '100%'
                  : '136%',
              zIndex: 11,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              right: 0,
              ...(isMobile() && { padding: 0 })
            }
          }
        : {
            style: {
              background: 'var(--v3-black-0)',
              ...(useFullContentHeight
                ? {
                    minHeight: contentHeight,
                    borderRadius: 6
                  }
                : {})
            }
          })}
      icon={
        <GlobalIcon
          lib="generic"
          name="noMatches"
          theme="colored"
          size={176}
          skeleton={{ useEmptyPlaceholder: true }}
        />
      }
      subTitle={
        <ResultSubTitle
          {...(!isMobile() && { ref: resultRef })}
          dangerouslySetInnerHTML={{
            __html: noGameText || t('sportsbook.noGameText')
          }}
        />
      }
      {...(withLink &&
        !isEvent && {
          extra: (
            <Button
              type="primary"
              size="large"
              onClick={() =>
                isESport
                  ? history.replace(
                      `${SpringConfigs.PAGE_URLS['esport']}/${
                        SpringConfigs.SPORTSBOOK_MOUNT_PATH
                      }/AllESports?type=${getType('l', true)}` ||
                        `${
                          window.location.origin +
                          menuJsonData.find(instance => {
                            return (
                              instance.type === 'esport' &&
                              instance.category === 'esport'
                            );
                          })?.path
                        }/${
                          SpringConfigs.SPORTSBOOK_MOUNT_PATH
                        }/AllESports?type=${getType('l', true)}`
                    )
                  : (window.location.href =
                      SpringConfigs.PAGE_URLS[getType('l', true)] ||
                      window.location.origin +
                        menuJsonData.find(instance => {
                          return (
                            instance.type === getType('l', true) ||
                            instance.category === getType('l', true)
                          );
                        })?.path)
              }
            >
              {t(`emptyMessages.emptyMatches${getType()}Action`)}
            </Button>
          )
        })}
    />
  );
};
